import React, { memo, useEffect, useRef, useState } from 'react';
import { useFormContext, FormProvider } from './FormContext';
import UniversalForm from './UniversalForm';
import {
  emissionAllocationConfig,
  gatheringConfig,
  globalInputsConfig,
  liquefactionConfig,
  processingConfig,
  productionConfig,
  shippingConfig,
  transmissionConfig,
} from './formConfig';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { apiService } from '../../../service/apiService';
import { EmissionTable } from './EmissionTable';
import EastIcon from '@mui/icons-material/East';
import { ResponseData } from './types';
import { generateEmissionSummary, steps, transformData } from './utils';
import { EnergyTable } from './EnergyTable';
import {
  EnergyFlowSankey,
  GHGIntensityBarChart,
  GHGIntensityPercentageChart,
  CO2AndMethaneIntensityChart,
} from './EnergyFlowSankey';
import './styles.scss';

const formConfigs = {
  globalInputsForm: globalInputsConfig,
  productionForm: productionConfig,
  gatheringForm: gatheringConfig,
  processingForm: processingConfig,
  transmissionForm: transmissionConfig,
  liquefactionForm: liquefactionConfig,
  shippingForm: shippingConfig,
  emissionAllocationForm: emissionAllocationConfig,
};

const generateOutputCharts = (response: any): string => {
  const { file_data, ...chartsData } = response;
  return chartsData;
};

const generateOutputFile = (response: any): string => {
  const binaryString = atob(response.file_data);
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return window.URL.createObjectURL(blob);
};

export const MainForm = memo(() => {
  const isTableHidden = true;
  const apiUrl =
    process.env.REACT_APP_API_URL || 'https://lngfunc.azurewebsites.net';
  const apiKey = process.env.REACT_APP_API_KEY;
  const { formData, formCompletionStatus, allFormsCompleted, processIniFiles } =
    useFormContext();
  const successBlockRef = useRef<HTMLDivElement>(null);

  const [selectedStep, setSelectedStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  const [responseData, setResponseData] = useState<ResponseData | null>(null);
  const [table1Data, setTable1Data] = useState<any | null>(null);
  const [table2Data, setTable2Data] = useState<any | null>(null);
  const [table3Data, setTable3Data] = useState<any | null>(null);
  const [chartsData, setChartsData] = useState<any | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const [outputFile, setOutputFile] = useState<{
    name: string;
    href: string;
  } | null>(null);

  const getConfig = async () => {
    try {
      const url = `${apiUrl}/api/data`;
      const result = await apiService.getRequest<ResponseData>(
        url,
        {},
        apiKey || '',
      );
      await processIniFiles(result);
      await setIsLoadingConfig(false);
    } catch (err: any) {
      setIsLoadingConfig(false);
      setError(err.message);
    }
  };

  useEffect(() => {
    setIsLoadingConfig(true);
    getConfig();
  }, []);

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);
    setHasScrolled(false);
    const preparedData = transformData(formData);

    try {
      const url = `${apiUrl}/api/lca`;
      const result = await apiService.postRequest<ResponseData>(
        url,
        preparedData,
        apiKey || '',
      );
      setIsLoading(false);
      setResponseData(result);

      const outputCharts = generateOutputCharts(result);

      const outputFileUrl = generateOutputFile(result);

      setOutputFile({
        name: 'output-file.xlsx',
        href: outputFileUrl,
      });

      setChartsData(outputCharts);

      const { table11, table12, table21, table22, table3 } =
        generateEmissionSummary(result);

      setTable1Data(
        formData.globalInputsForm.allocation_method_selection ===
          'First Allocation Method'
          ? table11
          : table12,
      );
      setTable2Data(
        formData.globalInputsForm.allocation_method_selection ===
          'First Allocation Method'
          ? table21
          : table22,
      );
      setTable3Data(table3);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message);
      setResponseData(null);
    }
  };

  useEffect(() => {
    if (
      table1Data &&
      table2Data &&
      table3Data &&
      successBlockRef.current &&
      !hasScrolled
    ) {
      const blockPosition =
        successBlockRef.current.getBoundingClientRect().top + window.scrollY;
      const offset = -10;
      window.scrollTo({ top: blockPosition + offset, behavior: 'smooth' });
      setHasScrolled(true);
    }
  }, [table1Data, table2Data, table3Data, hasScrolled]);

  return (
    <>
      {isLoadingConfig && (
        <div className="loader-overlay">
          <CircularProgress size={'5rem'} style={{ color: '#C15602' }} />
        </div>
      )}
      <Box sx={{ padding: '20px' }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          {steps.map((step, index) => (
            <>
              <Paper
                key={index}
                onClick={() => setSelectedStep(index)}
                style={{
                  padding: ' 5px 10px',
                  backgroundColor: formCompletionStatus[step.id]
                    ? '#66C2A5'
                    : '#D50000',
                  cursor: 'pointer',
                  color: 'white',
                  textAlign: 'center',
                  width: '110px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundClip: 'padding-box',
                  border:
                    selectedStep === index
                      ? '3px solid #0072B2'
                      : '3px solid #ddd',
                }}
              >
                <Typography>{step.title}</Typography>
              </Paper>
              <Paper
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: 'none',
                  padding: '0',
                  margin: '0',
                }}
              >
                <EastIcon />
              </Paper>
            </>
          ))}
          <Paper
            key={999}
            style={{
              padding: '0',
              cursor: 'pointer',
              color: 'white',
              border: 'none',
              boxShadow: 'none',
              textAlign: 'center',
            }}
          >
            <button
              onClick={handleSubmit}
              disabled={isLoading || !allFormsCompleted}
              style={{
                marginTop: '26px',
                padding: '10px 20px',
                backgroundColor:
                  isLoading || !allFormsCompleted ? '#ccc' : '#007BFF',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Run Model'}
            </button>
          </Paper>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ margin: '0 30px' }}>
          {error}
        </Alert>
      )}

      <Box sx={{ padding: '20px' }}>
        {steps[selectedStep] && (
          <UniversalForm
            config={formConfigs[steps[selectedStep].id]}
            formKey={steps[selectedStep].id}
            hide={false}
          />
        )}
      </Box>

      <div ref={successBlockRef}>
        {responseData && (
          <div style={{ padding: '20px 30px' }}>
            {outputFile && (
              <p>
                <b>Output report:</b>{' '}
                <a href={outputFile.href}>{outputFile.name}</a>
              </p>
            )}
            {!isTableHidden && (
              <EmissionTable
                title="GHG Emission Summary (Table 1) (unit: gCO2e/MJ LNG delivered)"
                columns={['Stage', 'CO2', 'CH4', 'Total']}
                rows={table1Data}
              />
            )}
            {!isTableHidden && (
              <EmissionTable
                title="GHG Emission Summary (Table 2) (unit: gCO2e/MJ LNG delivered)"
                columns={[
                  'Stage',
                  'Fuel Use',
                  'AGR',
                  'Flaring',
                  'Fugitives and Venting',
                  'Measurement Informed',
                  'Total',
                ]}
                rows={table2Data}
              />
            )}
            <EnergyTable rows={table3Data} title={'Energy Flow (MJ)'} />
          </div>
        )}
        {chartsData && (
          <>
            <EnergyFlowSankey
              data={chartsData}
              basin={formData.globalInputsForm.production_basin}
            />
            <GHGIntensityBarChart
              data={chartsData}
              basin={formData.globalInputsForm.production_basin}
              destination={formData.globalInputsForm.shipping_destination}
              tableData={table2Data}
            />
            <GHGIntensityPercentageChart
              data={chartsData}
              basin={formData.globalInputsForm.production_basin}
              destination={formData.globalInputsForm.shipping_destination}
              tableData={table2Data}
            />
            <CO2AndMethaneIntensityChart
              data={chartsData}
              type={'bar'}
              allocatedMethod={
                formData.globalInputsForm.allocation_method_selection
              }
            />
            <CO2AndMethaneIntensityChart
              data={chartsData}
              tableData={table1Data}
              type={'pie'}
              allocatedMethod={
                formData.globalInputsForm.allocation_method_selection
              }
            />
          </>
        )}
      </div>
    </>
  );
});

export const MainFormWithProvider = () => (
  <FormProvider>
    <MainForm />
  </FormProvider>
);
